// The QueryClient defaults (ADR 0005): staleTime 30s (the portal's Infinity is
// why it needed reloads), no refetch on focus, no retry on 4xx, one retry
// otherwise. Mutation errors go to an injectable hook (the integrator wires
// notify()); the http client itself never shows anything.
import { MutationCache, QueryClient } from '@tanstack/react-query'
import { HttpError } from './types'

export interface QueryClientOptions {
  onMutationError?: (error: unknown) => void
}

/** At most one retry, and none for an HTTP 4xx (nor for an abort). */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof HttpError && error.status >= 400 && error.status < 500) return false
  if (error instanceof DOMException && error.name === 'AbortError') return false
  return failureCount < 1
}

export function createQueryClient({ onMutationError }: QueryClientOptions = {}): QueryClient {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // A mutation that handles its own error (meta.silent) is not reported twice.
        if (mutation.meta?.silent === true) return
        onMutationError?.(error)
      }
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: shouldRetry
      },
      mutations: {
        retry: false
      }
    }
  })
}
